import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { Layout } from '../components/Layout';
import { Plus, UserCheck, UserX, Pencil, Trash2, X, AlertTriangle, ChevronLeft, ChevronRight, Key, Shield, Star, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PER_PAGE = 50;

// Protected admin email - matches backend
const PROTECTED_ADMIN_EMAIL = "anas@jsdc.in";

const isProtectedAdmin = (email) => email?.toLowerCase() === PROTECTED_ADMIN_EMAIL.toLowerCase();

const getRoleBadge = (role) => {
  const colors = {
    admin: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    sales: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    customer: 'bg-slate-50 text-slate-600 border-slate-200',
    production_team: 'bg-orange-50 text-orange-600 border-orange-200'
  };
  const displayNames = {
    admin: 'Admin',
    sales: 'Sales',
    customer: 'Customer',
    production_team: 'Production Team'
  };
  return <span className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-full border ${colors[role] || colors.customer}`}>{displayNames[role] || role}</span>;
};

const getPriceTierBadge = (tier) => {
  if (!tier) return null;
  const colors = {
    bulk: 'bg-amber-50 text-amber-600',
    setwise: 'bg-blue-50 text-blue-600',
    reselling: 'bg-purple-50 text-purple-600'
  };
  return <span className={`px-2 py-1 text-xs font-medium rounded ${colors[tier]}`}>{tier}</span>;
};

// Mobile Card Component - defined outside to prevent re-renders
const UserCard = ({ 
  user, 
  currentUserEmail,
  isAdmin,
  isSales,
  selectedUsers, 
  toggleSelectUser, 
  canManageUser,
  openEditModal,
  openDeleteModal,
  openPasswordModal,
  openSpecialPricingModal,
  toggleUserStatus
}) => {
  const isOwnAccount = user.email === currentUserEmail;
  const isProtected = isProtectedAdmin(user.email);
  const canSelect = !isOwnAccount && !isProtected;
  const isCustomer = user.role === 'customer';
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-3">
          {isAdmin && (
            <input
              type="checkbox"
              checked={selectedUsers.has(user.email)}
              onChange={() => toggleSelectUser(user.email)}
              disabled={!canSelect}
              className={`mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 ${!canSelect ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          )}
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-900">{user.name}</p>
              {isProtected && (
                <span title="System Owner - Protected" className="inline-flex items-center">
                  <Shield className="w-4 h-4 text-amber-500" />
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 break-all">{user.email}</p>
          </div>
        </div>
        {user.is_active ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Active</span>
        ) : (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-rose-50 text-rose-600 border border-rose-200">Inactive</span>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        {isAdmin && getRoleBadge(user.role)}
        {getPriceTierBadge(user.price_tier)}
      </div>
      
      {canManageUser(user) && (
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            onClick={() => openEditModal(user)}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md font-medium transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 text-sm"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Edit
          </button>
          {/* Special Pricing - Admin/Sales for customers only */}
          {isCustomer && (isAdmin || isSales) && (
            <button
              onClick={() => openSpecialPricingModal(user)}
              data-testid={`special-pricing-${user.email}`}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md font-medium transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 text-sm"
            >
              <Star className="w-4 h-4 mr-1.5" /> Pricing
            </button>
          )}
          {/* Password change - Admin only */}
          {isAdmin && (
            <button
              onClick={() => openPasswordModal(user)}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md font-medium transition-colors bg-purple-50 text-purple-600 hover:bg-purple-100 text-sm"
            >
              <Key className="w-4 h-4 mr-1.5" /> Password
            </button>
          )}
          {/* Delete button - hidden for protected admin */}
          {!isProtected && (
            <button
              onClick={() => openDeleteModal(user)}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md font-medium transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 text-sm"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </button>
          )}
          {/* Toggle status - hidden for protected admin */}
          {!isProtected && (
            <button
              onClick={() => toggleUserStatus(user.email, user.is_active)}
              className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md font-medium transition-colors text-sm ${
                user.is_active
                  ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                  : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
              }`}
            >
              {user.is_active ? (
                <><UserX className="w-4 h-4 mr-1.5" /> Deactivate</>
              ) : (
                <><UserCheck className="w-4 h-4 mr-1.5" /> Activate</>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'customer',
    price_tier: 'bulk',
    password: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    price_tier: '',
    is_active: true
  });
  const [passwordFormData, setPasswordFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Special pricing state
  const [showSpecialPricingModal, setShowSpecialPricingModal] = useState(false);
  const [specialPricingCustomer, setSpecialPricingCustomer] = useState(null);
  const [specialPrices, setSpecialPrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingSpecialPrices, setLoadingSpecialPrices] = useState(false);
  const [showAddSpecialPrice, setShowAddSpecialPrice] = useState(false);
  const [specialPriceForm, setSpecialPriceForm] = useState({ design_number: '', special_price: '' });
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebounce(productSearch, 300);
  const [productLoading, setProductLoading] = useState(false);
  const [savingSpecialPrice, setSavingSpecialPrice] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const isSales = currentUser?.role === 'sales';

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users?page=${page}&per_page=${PER_PAGE}`);
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotalUsers(response.data.total);
      setCurrentPage(response.data.page);
      // Clear selections when page changes
      setSelectedUsers(new Set());
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [fetchUsers, currentPage]);

  // Server-side debounced product search (only used when special pricing modal is open)
  const fetchProducts = useCallback(async (search = '') => {
    setProductLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', per_page: '30' });
      if (search && search.trim()) params.set('search', search.trim());
      const r = await axios.get(`${API}/products?${params}`);
      setProducts(Array.isArray(r.data?.products) ? r.data.products : []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  // Fetch special prices for a customer
  const fetchSpecialPrices = useCallback(async (customerEmail) => {
    setLoadingSpecialPrices(true);
    try {
      const response = await axios.get(`${API}/special-pricing/customer/${encodeURIComponent(customerEmail)}`);
      setSpecialPrices(response.data.special_prices || []);
    } catch (error) {
      console.error('Failed to fetch special prices:', error);
      setSpecialPrices([]);
    } finally {
      setLoadingSpecialPrices(false);
    }
  }, []);

  // Open special pricing modal
  const openSpecialPricingModal = async (user) => {
    setSpecialPricingCustomer(user);
    setShowSpecialPricingModal(true);
    setShowAddSpecialPrice(false);
    setSpecialPriceForm({ design_number: '', special_price: '' });
    setProductSearch('');
    
    // Fetch products (initial top-30) and special prices
    await Promise.all([
      fetchProducts(''),
      fetchSpecialPrices(user.email)
    ]);
  };

  // Re-fetch products when debounced search changes (only while pricing modal is open)
  useEffect(() => {
    if (showSpecialPricingModal) {
      fetchProducts(debouncedProductSearch);
    }
  }, [debouncedProductSearch, showSpecialPricingModal, fetchProducts]);

  // Add new special price
  const handleAddSpecialPrice = async (e) => {
    e.preventDefault();
    if (!specialPricingCustomer || !specialPriceForm.design_number || !specialPriceForm.special_price) return;
    
    setSavingSpecialPrice(true);
    try {
      await axios.post(`${API}/special-pricing`, {
        customer_email: specialPricingCustomer.email,
        design_number: specialPriceForm.design_number,
        special_price: parseFloat(specialPriceForm.special_price)
      });
      toast.success('Special price added successfully');
      setShowAddSpecialPrice(false);
      setSpecialPriceForm({ design_number: '', special_price: '' });
      setProductSearch('');
      fetchSpecialPrices(specialPricingCustomer.email);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add special price');
    } finally {
      setSavingSpecialPrice(false);
    }
  };

  // Delete special price
  const handleDeleteSpecialPrice = async (designNumber) => {
    if (!specialPricingCustomer) return;
    if (!window.confirm('Are you sure you want to remove this special price?')) return;
    
    try {
      await axios.delete(`${API}/special-pricing/${encodeURIComponent(specialPricingCustomer.email)}/${encodeURIComponent(designNumber)}`);
      toast.success('Special price removed');
      fetchSpecialPrices(specialPricingCustomer.email);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove special price');
    }
  };

  // Exclude products that already have a special price for this customer.
  // Server has already filtered by search query.
  const filteredProductsForPricing = (Array.isArray(products) ? products : []).filter(p => {
    const hasSpecialPrice = (Array.isArray(specialPrices) ? specialPrices : []).some(sp => sp.design_number === p.design_number);
    return !hasSpecialPrice;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/users`, formData);
      toast.success('User created successfully');
      setShowModal(false);
      setFormData({ email: '', name: '', role: 'customer', price_tier: 'bulk', password: '' });
      fetchUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) return;
    
    try {
      const updateData = {
        name: editFormData.name,
        is_active: editFormData.is_active
      };
      
      if (editFormData.role === 'customer' || selectedUser.role === 'customer') {
        updateData.price_tier = editFormData.price_tier;
      }
      
      if (isAdmin && editFormData.role !== selectedUser.role) {
        updateData.role = editFormData.role;
      }
      
      await axios.put(`${API}/users/${selectedUser.email}`, updateData);
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    // Extra protection in UI
    if (isProtectedAdmin(selectedUser.email)) {
      toast.error('Cannot delete the system owner account');
      setShowDeleteModal(false);
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${selectedUser.email}`);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      role: user.role,
      price_tier: user.price_tier || 'bulk',
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    // Don't allow opening delete modal for protected admin
    if (isProtectedAdmin(user.email)) {
      toast.error('Cannot delete the system owner account');
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordFormData({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) return;
    
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordFormData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/users/${selectedUser.email}/change-password`, {
        new_password: passwordFormData.new_password,
        confirm_password: passwordFormData.confirm_password
      });
      toast.success(`Password changed for ${selectedUser.name}`);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordFormData({ new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const toggleUserStatus = async (email, currentStatus) => {
    // Protect system owner
    if (isProtectedAdmin(email)) {
      toast.error('Cannot disable the system owner account');
      return;
    }
    
    try {
      await axios.patch(`${API}/users/${email}/toggle-active`);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Bulk selection handlers
  const toggleSelectUser = (email) => {
    // Don't allow selecting own account or protected admin
    if (email === currentUser?.email) return;
    if (isProtectedAdmin(email)) return;
    
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    // Filter out current user's email AND protected admin
    const selectableUsers = users.filter(u => 
      u.email !== currentUser?.email && !isProtectedAdmin(u.email)
    );
    
    if (selectedUsers.size === selectableUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(selectableUsers.map(u => u.email)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/users/bulk-delete`, {
        emails: Array.from(selectedUsers)
      });
      toast.success(response.data.message);
      setShowBulkDeleteModal(false);
      setSelectedUsers(new Set());
      fetchUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white border border-slate-200 rounded-lg">
        <div className="text-sm text-slate-600">
          Showing {((currentPage - 1) * PER_PAGE) + 1} - {Math.min(currentPage * PER_PAGE, totalUsers)} of {totalUsers} users
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => goToPage(1)}
                className="px-3 py-1 rounded-md hover:bg-slate-100 text-sm"
              >
                1
              </button>
              {startPage > 2 && <span className="px-2 text-slate-400">...</span>}
            </>
          )}
          
          {pages.map(page => (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`px-3 py-1 rounded-md text-sm ${
                page === currentPage
                  ? 'bg-indigo-600 text-white'
                  : 'hover:bg-slate-100'
              }`}
            >
              {page}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2 text-slate-400">...</span>}
              <button
                onClick={() => goToPage(totalPages)}
                className="px-3 py-1 rounded-md hover:bg-slate-100 text-sm"
              >
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  const canManageUser = (targetUser) => {
    if (isAdmin) return true;
    if (isSales && targetUser.role === 'customer') return true;
    return false;
  };

  const getPageTitle = () => {
    if (isSales) {
      return { title: 'Customer Management', subtitle: 'Manage your customers' };
    }
    return { title: 'User Management', subtitle: 'Manage team members and customers' };
  };

  const { title, subtitle } = getPageTitle();

  const getRoleOptions = () => {
    if (isAdmin) {
      return (
        <>
          <option value="customer">Customer</option>
          <option value="sales">Sales</option>
          <option value="production_team">Production Team</option>
          <option value="admin">Admin</option>
        </>
      );
    }
    return <option value="customer">Customer</option>;
  };

  // Get selectable users count (excluding own account AND protected admin)
  const selectableUsersCount = users.filter(u => 
    u.email !== currentUser?.email && !isProtectedAdmin(u.email)
  ).length;

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="users-title">
              {title}
            </h1>
            <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              {subtitle}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Bulk Delete Button - Admin Only */}
            {isAdmin && selectedUsers.size > 0 && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                data-testid="bulk-delete-users-button"
                className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Delete Selected ({selectedUsers.size})
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              data-testid="create-user-button"
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] text-sm sm:text-base"
            >
              <Plus className="w-5 h-5 mr-2" />
              {isSales ? 'Add Customer' : 'Add User'}
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-sm sm:text-base text-slate-600">Loading users...</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {/* Mobile Select All - Admin Only */}
              {isAdmin && users.length > 0 && (
                <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={selectedUsers.size === selectableUsersCount && selectableUsersCount > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600">Select all on this page</span>
                </div>
              )}
              {users.map((user) => (
                <UserCard 
                  key={user.email} 
                  user={user}
                  currentUserEmail={currentUser?.email}
                  isAdmin={isAdmin}
                  isSales={isSales}
                  selectedUsers={selectedUsers}
                  toggleSelectUser={toggleSelectUser}
                  canManageUser={canManageUser}
                  openEditModal={openEditModal}
                  openDeleteModal={openDeleteModal}
                  openPasswordModal={openPasswordModal}
                  openSpecialPricingModal={openSpecialPricingModal}
                  toggleUserStatus={toggleUserStatus}
                />
              ))}
              {users.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No {isSales ? 'customers' : 'users'} found
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="users-table">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      {isAdmin && (
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedUsers.size === selectableUsersCount && selectableUsersCount > 0}
                            onChange={toggleSelectAll}
                            data-testid="select-all-users-checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </th>
                      )}
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                      {isAdmin && (
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                      )}
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Price Tier</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {users.map((user) => {
                      const isOwnAccount = user.email === currentUser?.email;
                      const isProtected = isProtectedAdmin(user.email);
                      const canSelect = !isOwnAccount && !isProtected;
                      
                      return (
                        <tr key={user.email} data-testid={`user-row-${user.email}`} className="hover:bg-slate-50 transition-colors">
                          {isAdmin && (
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={selectedUsers.has(user.email)}
                                onChange={() => toggleSelectUser(user.email)}
                                disabled={!canSelect}
                                data-testid={`select-user-${user.email}`}
                                className={`h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 ${!canSelect ? 'opacity-50 cursor-not-allowed' : ''}`}
                              />
                            </td>
                          )}
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              {user.name}
                              {isOwnAccount && <span className="text-xs text-slate-400">(You)</span>}
                              {isProtected && (
                                <span title="System Owner - Protected" className="inline-flex items-center">
                                  <Shield className="w-4 h-4 text-amber-500" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-slate-600">{user.email}</td>
                          {isAdmin && (
                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap">{getRoleBadge(user.role)}</td>
                          )}
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap">{getPriceTierBadge(user.price_tier)}</td>
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                            {user.is_active ? (
                              <span className="px-2 sm:px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Active</span>
                            ) : (
                              <span className="px-2 sm:px-3 py-1 text-xs font-medium rounded-full bg-rose-50 text-rose-600 border border-rose-200">Inactive</span>
                            )}
                          </td>
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-1 lg:gap-2">
                              {canManageUser(user) && (
                                <>
                                  <button
                                    onClick={() => openEditModal(user)}
                                    data-testid={`edit-user-${user.email}`}
                                    className="inline-flex items-center px-2 lg:px-3 py-1 rounded-md font-medium transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs lg:text-sm"
                                  >
                                    <Pencil className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1" /> <span className="hidden lg:inline">Edit</span>
                                  </button>
                                  {/* Special Pricing - Admin/Sales for customers only */}
                                  {user.role === 'customer' && (isAdmin || isSales) && (
                                    <button
                                      onClick={() => openSpecialPricingModal(user)}
                                      data-testid={`special-pricing-${user.email}`}
                                      className="inline-flex items-center px-2 lg:px-3 py-1 rounded-md font-medium transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs lg:text-sm"
                                    >
                                      <Star className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1" /> <span className="hidden lg:inline">Pricing</span>
                                    </button>
                                  )}
                                  {/* Password change - Admin only */}
                                  {isAdmin && (
                                    <button
                                      onClick={() => openPasswordModal(user)}
                                      data-testid={`password-user-${user.email}`}
                                      className="inline-flex items-center px-2 lg:px-3 py-1 rounded-md font-medium transition-colors bg-purple-50 text-purple-600 hover:bg-purple-100 text-xs lg:text-sm"
                                    >
                                      <Key className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1" /> <span className="hidden lg:inline">Password</span>
                                    </button>
                                  )}
                                  {/* Delete button - hidden for protected admin */}
                                  {!isProtected && (
                                    <button
                                      onClick={() => openDeleteModal(user)}
                                      data-testid={`delete-user-${user.email}`}
                                      className="inline-flex items-center px-2 lg:px-3 py-1 rounded-md font-medium transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 text-xs lg:text-sm"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1" /> <span className="hidden lg:inline">Delete</span>
                                    </button>
                                  )}
                                  {/* Toggle status - hidden for protected admin */}
                                  {!isProtected && (
                                    <button
                                      onClick={() => toggleUserStatus(user.email, user.is_active)}
                                      data-testid={`toggle-user-${user.email}`}
                                      className={`inline-flex items-center px-2 lg:px-3 py-1 rounded-md font-medium transition-colors text-xs lg:text-sm ${
                                        user.is_active
                                          ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                                          : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                                      }`}
                                    >
                                      {user.is_active ? (
                                        <><UserX className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1" /> <span className="hidden lg:inline">Deactivate</span></>
                                      ) : (
                                        <><UserCheck className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1" /> <span className="hidden lg:inline">Activate</span></>
                                      )}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No {isSales ? 'customers' : 'users'} found
                </div>
              )}
            </div>

            {/* Pagination */}
            {renderPagination()}
          </>
        )}

        {/* Create User Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  {isSales ? 'Create New Customer' : 'Create New User'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4" data-testid="create-user-form">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    data-testid="user-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    data-testid="user-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    data-testid="user-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      data-testid="user-role-select"
                      className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    >
                      {getRoleOptions()}
                    </select>
                  </div>
                )}
                {(formData.role === 'customer' || isSales) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Price Tier</label>
                    <select
                      value={formData.price_tier}
                      onChange={(e) => setFormData({ ...formData, price_tier: e.target.value })}
                      data-testid="user-price-tier-select"
                      className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    >
                      <option value="bulk">Bulk</option>
                      <option value="setwise">Set-wise</option>
                      <option value="reselling">Reselling</option>
                    </select>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 pt-4">
                  <button
                    type="submit"
                    data-testid="submit-user-button"
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] text-base"
                  >
                    {isSales ? 'Create Customer' : 'Create User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    data-testid="cancel-user-button"
                    className="px-4 py-2.5 sm:py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowEditModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Edit User</h2>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="p-4 sm:p-6 space-y-4" data-testid="edit-user-form">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    disabled
                    value={selectedUser.email}
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 cursor-not-allowed text-base"
                  />
                  <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    data-testid="edit-user-name-input"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                    <select
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                      data-testid="edit-user-role-select"
                      className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    >
                      <option value="customer">Customer</option>
                      <option value="sales">Sales</option>
                      <option value="production_team">Production Team</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                )}
                {(editFormData.role === 'customer' || selectedUser.role === 'customer') && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Price Tier</label>
                    <select
                      value={editFormData.price_tier}
                      onChange={(e) => setEditFormData({ ...editFormData, price_tier: e.target.value })}
                      data-testid="edit-user-price-tier-select"
                      className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    >
                      <option value="bulk">Bulk</option>
                      <option value="setwise">Set-wise</option>
                      <option value="reselling">Reselling</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    value={editFormData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.value === 'active' })}
                    data-testid="edit-user-status-select"
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-4">
                  <button
                    type="submit"
                    data-testid="submit-edit-user-button"
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-all active:scale-[0.98] text-base"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2.5 sm:py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowDeleteModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-rose-100">
                  <AlertTriangle className="w-6 h-6 text-rose-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Delete User</h2>
                <p className="text-slate-600 text-center mb-6 text-sm sm:text-base">
                  Are you sure you want to delete <strong>{selectedUser.name}</strong> ({selectedUser.email})?
                  <br />
                  <span className="text-rose-600 text-sm">This action cannot be undone.</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    data-testid="confirm-delete-user-button"
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-all active:scale-[0.98] text-base order-1 sm:order-2"
                  >
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowBulkDeleteModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-rose-100">
                  <AlertTriangle className="w-6 h-6 text-rose-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Delete Users</h2>
                <p className="text-slate-600 text-center mb-2 text-sm sm:text-base">
                  Are you sure you want to delete <strong>{selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}</strong>?
                </p>
                <p className="text-amber-600 text-center text-sm mb-2">
                  Note: Historical orders will be preserved.
                </p>
                <p className="text-rose-600 text-center text-sm mb-6">
                  This action cannot be undone.
                </p>
                <div className="max-h-32 overflow-y-auto mb-4 bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-2">Users to be deleted:</p>
                  <div className="flex flex-wrap gap-1">
                    {Array.from(selectedUsers).map(email => (
                      <span key={email} className="px-2 py-1 bg-white border border-slate-200 rounded text-xs text-slate-700 truncate max-w-[150px]" title={email}>
                        {email}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowBulkDeleteModal(false)}
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    data-testid="confirm-bulk-delete-users-button"
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-all active:scale-[0.98] text-base order-1 sm:order-2 disabled:opacity-50"
                  >
                    {bulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.size} User${selectedUsers.size > 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Change Password Modal - Admin Only */}
        {showPasswordModal && selectedUser && isAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowPasswordModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-purple-600" />
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Change Password</h2>
                </div>
                <button onClick={() => setShowPasswordModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handlePasswordChange} className="p-4 sm:p-6 space-y-4" data-testid="change-password-form">
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-slate-600">
                    Changing password for: <strong>{selectedUser.name}</strong>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{selectedUser.email}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                    value={passwordFormData.new_password}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, new_password: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                    value={passwordFormData.confirm_password}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      The user will need to use this new password on their next login.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 pt-4">
                  <button
                    type="submit"
                    disabled={changingPassword || passwordFormData.new_password !== passwordFormData.confirm_password}
                    data-testid="submit-password-button"
                    className="flex-1 px-4 py-2.5 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium transition-all active:scale-[0.98] text-base disabled:opacity-50"
                  >
                    {changingPassword ? 'Changing Password...' : 'Change Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="px-4 py-2.5 sm:py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Special Pricing Modal */}
        {showSpecialPricingModal && specialPricingCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowSpecialPricingModal(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Special Pricing</h2>
                </div>
                <button onClick={() => setShowSpecialPricingModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[70vh]">
                {/* Customer Info */}
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-slate-600">
                    Customer: <strong>{specialPricingCustomer.name}</strong>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{specialPricingCustomer.email}</p>
                  <p className="text-xs text-slate-500">
                    Default Price Tier: <span className="font-medium capitalize">{specialPricingCustomer.price_tier || 'bulk'}</span>
                  </p>
                </div>

                {/* Add New Special Price Button */}
                {!showAddSpecialPrice && (
                  <button
                    onClick={() => setShowAddSpecialPrice(true)}
                    data-testid="add-special-price-button"
                    className="w-full mb-4 px-4 py-2.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium transition-all active:scale-[0.98] flex items-center justify-center text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Special Price
                  </button>
                )}

                {/* Add Special Price Form */}
                {showAddSpecialPrice && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-amber-800 mb-3">Add New Special Price</h3>
                    <form onSubmit={handleAddSpecialPrice} className="space-y-3">
                      {/* Product Search */}
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Search Product</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search by design number or name..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Product Selection */}
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Select Product</label>
                        <select
                          value={specialPriceForm.design_number}
                          onChange={(e) => setSpecialPriceForm({ ...specialPriceForm, design_number: e.target.value })}
                          data-testid="special-price-product-select"
                          className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                          required
                        >
                          <option value="">{productLoading ? 'Searching…' : 'Select a product...'}</option>
                          {filteredProductsForPricing.map((product) => (
                            <option key={product.design_number} value={product.design_number}>
                              {product.design_number} - {product.product_name} (₹{product.pricing[specialPricingCustomer.price_tier || 'bulk']})
                            </option>
                          ))}
                        </select>
                        {filteredProductsForPricing.length === 0 && productSearch && (
                          <p className="text-xs text-slate-500 mt-1">No matching products found</p>
                        )}
                        {filteredProductsForPricing.length === 0 && !productSearch && (
                          <p className="text-xs text-slate-500 mt-1">All products already have special prices</p>
                        )}
                      </div>
                      
                      {/* Special Price */}
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Special Price (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Enter special price"
                          value={specialPriceForm.special_price}
                          onChange={(e) => setSpecialPriceForm({ ...specialPriceForm, special_price: e.target.value })}
                          data-testid="special-price-input"
                          className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                          required
                        />
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <button
                          type="submit"
                          disabled={savingSpecialPrice || !specialPriceForm.design_number || !specialPriceForm.special_price}
                          data-testid="save-special-price-button"
                          className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium transition-all active:scale-[0.98] text-sm disabled:opacity-50"
                        >
                          {savingSpecialPrice ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddSpecialPrice(false);
                            setSpecialPriceForm({ design_number: '', special_price: '' });
                            setProductSearch('');
                          }}
                          className="px-4 py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Existing Special Prices List */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    Current Special Prices ({specialPrices.length})
                  </h3>
                  
                  {loadingSpecialPrices ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-slate-500">Loading...</p>
                    </div>
                  ) : specialPrices.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <Star className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">No special prices set</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Default tier prices will be used for all products
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {specialPrices.map((sp) => (
                        <div
                          key={sp.design_number}
                          className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg"
                          data-testid={`special-price-item-${sp.design_number}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">
                              {sp.product_name || sp.design_number}
                            </p>
                            <p className="text-xs text-slate-500">#{sp.design_number}</p>
                          </div>
                          <div className="flex items-center gap-3 ml-3">
                            <div className="text-right">
                              <p className="font-bold text-amber-600">₹{sp.special_price}</p>
                            </div>
                            <button
                              onClick={() => handleDeleteSpecialPrice(sp.design_number)}
                              className="p-1.5 text-rose-600 hover:bg-rose-50 rounded transition-colors"
                              title="Remove special price"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4 sm:p-6 border-t border-slate-200 sticky bottom-0 bg-white">
                <button
                  onClick={() => setShowSpecialPricingModal(false)}
                  className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
